// src/services/apaleo/city-tax-hrs.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { ReservationModel, ServiceOfferModel, AutomationResultRow, AutomationResult, ToolUser, LogFn, OnLogFn, OnProgressFn } from './types';
import type { ApaleoClient } from './client-contract';
import { CITY_TAX_TOOL_PROPERTY_ID, errorMessage } from './config';

const part: ThisType<ApaleoClient> = {
async splitHrsClubRates({
  dateFrom = null,
  dateTo = null,
  reservationId = null,
  sources = null,
  createdFrom = null,
  createdTo = null,
  breakfastPerPerson = null,
  breakfastServiceId = null,
  dryRun = true,
  user = null,
  onProgress = null,
  onLog = null,
}: {
  dateFrom?: string | null;
  dateTo?: string | null;
  reservationId?: string | null;
  sources?: string[] | string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  breakfastPerPerson?: number | string | null;
  breakfastServiceId?: string | null;
  dryRun?: boolean;
  user?: ToolUser | null;
  onProgress?: OnProgressFn | null;
  onLog?: OnLogFn | null;
} = {}): Promise<AutomationResult> {
  const singleMode =
    typeof reservationId === "string" && reservationId.trim().length > 0;
  const singleReservationId = singleMode ? (reservationId as string).trim() : null;

  // Breakfast amount is the core input of this tool — validate hard.
  const breakfastAmount = Number(breakfastPerPerson);
  if (!Number.isFinite(breakfastAmount) || breakfastAmount <= 0) {
    throw new Error(
      "splitHrsClubRates: breakfastPerPerson must be a positive number (e.g. 12.00)",
    );
  }

  const { sourceFilter, createdFromTs, createdToTs } =
    this._normalizeAuditFilters(sources, createdFrom, createdTo);

  const operatorLabel =
    (user && (user.name || user.id || user.email))
      ? `${user.name || user.id || user.email}`
      : "system";
  const log: LogFn = (level, message) => {
    const decorated = `[splitHrsClubRates] (${operatorLabel}) ${message}`;
    if (typeof onLog === "function") {
      try { onLog({ level, message: decorated }); } catch { /* ignore listener errors */ }
    }
  };

  const result: AutomationResult = {
    tool: "splitHrsClubRates",
    dryRun: !!dryRun,
    dateFrom,
    dateTo,
    mode: singleMode ? "single" : "range",
    reservationId: singleReservationId,
    sourceFilter,
    createdFrom,
    createdTo,
    breakfastPerPerson: breakfastAmount,
    breakfastServiceId: breakfastServiceId || null,
    processed: 0,
    skipped: 0,
    failed: 0,
    results: [],
  };

  let reservations: ReservationModel[];
  if (singleMode) {
    log("log", `Single-reservation mode: fetching ${singleReservationId} (dryRun=${dryRun})`);
    const single = await this.fetchReservationById(singleReservationId || "");
    if (!single) {
      throw new Error(`splitHrsClubRates: reservation ${singleReservationId} not found`);
    }
    reservations = [single];
  } else {
    if (!dateFrom || !dateTo) {
      throw new Error("splitHrsClubRates: dateFrom and dateTo are required");
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      throw new Error("splitHrsClubRates: dateFrom must be <= dateTo");
    }
    log("log", `Fetching reservations with arrival in [${dateFrom}, ${dateTo}] (dryRun=${dryRun})`);
    // BERLIN-only scoping (fetch level) — same rationale as recalculateCityTaxes.
    reservations = await this.fetchReservations(
      dateFrom,
      dateTo,
      "Arrival",
      "timeSlices,totalAmount,totalGrossAmount,totalNetAmount",
      "",
      CITY_TAX_TOOL_PROPERTY_ID,
    );
  }

  if (!reservations || reservations.length === 0) {
    log("warn", "No reservations in the selected period.");
    return result;
  }

  const total = reservations.length;
  log("log", `Found ${total} reservation(s) in the period.`);

  // Same DIVISOR identity as recalculateCityTaxes: sending accommodation
  // = gross / 1.075 makes Apaleo's recomputed tax bring the folio back
  // to the input gross (±1¢; Berlin model — the city-tax line itself
  // carries 7% VAT, so gross = net * 1.07 * 1.075). Here the input to
  // that identity is the rate MINUS breakfast, so the breakfast service
  // closes the circle.
  const DIVISOR = 1.075; // gross = net * 1.07 (VAT) * 1.075 (city tax)
  const round2 = (n: number) => Number(n.toFixed(2));

  for (let i = 0; i < reservations.length; i++) {
    const res = reservations[i];
    const guestName = `${res.primaryGuest?.firstName || ""} ${res.primaryGuest?.lastName || ""}`.trim() || res.id;
    const source = (res.source || "").trim();
    const channelCode = (res.channelCode || "").trim();
    const channel = source || channelCode || "Direct";

    // Display-safe property id for the skip reason log below.
    const propertyId = (res.property?.id || "").trim();

    const currency =
      res.totalAmount?.currency ||
      res.totalGrossAmount?.currency ||
      res.totalNetAmount?.currency ||
      "EUR";
    const expandedTotal: number | null =
      res.totalAmount?.amount != null
        ? Number(res.totalAmount.amount)
        : res.totalGrossAmount?.amount != null
          ? Number(res.totalGrossAmount.amount)
          : res.totalNetAmount?.amount != null
            ? Number(res.totalNetAmount.amount)
            : null;

    if (typeof onProgress === "function") {
      try { onProgress({ processed: i, total, current: res.id || null }); } catch { /* noop */ }
    }

    const skipRow = (reason: string, extra: Partial<AutomationResultRow> = {}) => {
      result.skipped++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "skipped",
        reason,
        originalTotal: expandedTotal,
        newTotal: expandedTotal,
        currency,
        ...extra,
      });
    };

    // 0. Property gate (BERLIN-only tool) — row-level backstop, covers
    // single-reservation mode. Runs before the audit filters; fail-closed
    // on unknown property.
    if (propertyId !== CITY_TAX_TOOL_PROPERTY_ID) {
      skipRow("property_not_berlin");
      log("log", `Skip ${res.id} (${guestName}): property='${propertyId || "unknown"}' — tool is BERLIN-only`);
      continue;
    }

    // 0. Booking-source filter
    if (sourceFilter && !sourceFilter.includes(source)) {
      skipRow("source_not_selected");
      log("log", `Skip ${res.id} (${guestName}): source='${source}' not in [${sourceFilter.join(", ")}]`);
      continue;
    }

    // 0b. Creation-date window (fail-closed on missing/invalid `created`)
    if (createdFromTs || createdToTs) {
      const createdTs = res.created ? Date.parse(res.created) : NaN;
      if (Number.isNaN(createdTs)) {
        skipRow("created_missing_or_invalid");
        log("warn", `Skip ${res.id} (${guestName}): created timestamp missing or invalid`);
        continue;
      }
      const createdLocal = new Date(createdTs);
      const createdDayStart = new Date(
        createdLocal.getFullYear(),
        createdLocal.getMonth(),
        createdLocal.getDate(),
      ).getTime();
      if (createdFromTs && createdDayStart < createdFromTs.getTime()) {
        skipRow("created_before_window");
        log("log", `Skip ${res.id} (${guestName}): created ${res.created} before ${createdFrom}`);
        continue;
      }
      if (createdToTs && createdDayStart > createdToTs.getTime()) {
        skipRow("created_after_window");
        log("log", `Skip ${res.id} (${guestName}): created ${res.created} after ${createdTo}`);
        continue;
      }
    }

    // 0c. Status gate — mirrors recalculateCityTaxes: ONLY Confirmed
    //     reservations are touched (canceled rows still appear in
    //     arrival-window fetches; amending one would resurrect charges).
    //     Fail-closed on missing status; also protects single-reservation
    //     mode (fetch-by-ID has no status filter).
    if (res.status !== "Confirmed") {
      skipRow("status_not_confirmed");
      log("log", `Skip ${res.id} (${guestName}): status='${res.status || "unknown"}' — only Confirmed reservations are processed`);
      continue;
    }

    // 1. Channel gate — same pipeline as the city-tax recalc tool
    if (channelCode !== "ChannelManager") {
      skipRow("channel_not_channelmanager");
      log("log", `Skip ${res.id} (${guestName}): channel=${channel}`);
      continue;
    }

    // 1b. Idempotency gate — same cross-tool discipline as
    //     recalculateCityTaxes: a reservation already stamped by a
    //     previous run of EITHER tool (comment marker) is skipped so the
    //     amend + breakfast booking never run twice.
    if (this._hasAutomationMarker(res.comment)) {
      skipRow("already_processed_by_automation");
      log("log", `Skip ${res.id} (${guestName}): automation comment already present — not running again`);
      continue;
    }

    // 2. Time slices
    const timeSlices = res.timeSlices || [];
    if (timeSlices.length === 0) {
      skipRow("no_time_slices");
      log("warn", `Skip ${res.id} (${guestName}): no time slices`);
      continue;
    }
    const slicePrices: number[] = timeSlices.map((ts) => {
      const raw =
        ts?.baseAmount?.grossAmount ??
        ts?.totalAmount?.amount ??
        ts?.totalGrossAmount?.amount ??
        null;
      if (raw == null) return NaN;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : NaN;
    });
    if (slicePrices.some((p) => p === null || Number.isNaN(p))) {
      skipRow("invalid_time_slice_price");
      log("warn", `Skip ${res.id} (${guestName}): invalid time slice price(s)`);
      continue;
    }
    const originalTotal = expandedTotal != null
      ? expandedTotal
      : slicePrices.reduce((acc, p) => acc + p, 0);

    // 3. Split math. Breakfast counts per ADULT per night (matches the
    //    ServiceQuickBooking "Frühstück = Erw-Anzahl" convention).
    const guests = Math.max(1, Number(res.adults) || 1);
    const nights = timeSlices.length;
    const breakfastPerNight = round2(breakfastAmount * guests);
    const totalBreakfast = round2(breakfastPerNight * nights);
    if (slicePrices.some((g) => g - breakfastPerNight <= 0)) {
      skipRow("breakfast_exceeds_rate", { breakfastPerNight, totalBreakfast });
      log("warn", `Skip ${res.id} (${guestName}): breakfast ${breakfastPerNight} ≥ per-night rate`);
      continue;
    }
    // Accommodation that keeps folio total ≈ rate − breakfast + breakfast:
    const newPerNightPrices = slicePrices.map((g) =>
      round2((g - breakfastPerNight) / DIVISOR),
    );
    const totalNewAccommodation = round2(
      newPerNightPrices.reduce((a, b) => a + b, 0),
    );
    const projectedCityTaxPerNight = newPerNightPrices.map((accom) =>
      round2(accom * 0.075),
    );
    const totalProjectedCityTax = round2(
      projectedCityTaxPerNight.reduce((a, b) => a + b, 0),
    );
    // Post-amend folio ≈ rate − breakfast; + breakfast service ≈ rate.
    const projectedPostAmendTotal = round2(originalTotal - totalBreakfast);
    const projectedNewTotal = round2(
      totalNewAccommodation + totalProjectedCityTax + totalBreakfast,
    );

    if (dryRun) {
      result.processed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "dryRun",
        originalTotal,
        newTotal: projectedNewTotal,
        nights,
        guests,
        breakfastPerNight,
        totalBreakfast,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: totalNewAccommodation,
        totalCityTax: totalProjectedCityTax,
        currency,
      });
      log(
        "log",
        `Dry-run ${res.id} (${guestName}): ${nights} night(s) × ${guests} guest(s), ` +
        `breakfast ${totalBreakfast.toFixed(2)} ${currency}, accommodation ${totalNewAccommodation.toFixed(2)} ${currency}, ` +
        `projected city tax ${totalProjectedCityTax.toFixed(2)} ${currency}, folio ≈ ${projectedNewTotal.toFixed(2)} ${currency} ` +
        `(imported ${originalTotal.toFixed(2)} ${currency})`,
      );
      continue;
    }

    // 4. Real run: amend accommodation down, then book breakfast service.
    try {
      await this._amendAccommodationPrices(
        res,
        newPerNightPrices,
        projectedPostAmendTotal,
        currency,
        log,
      );

      // Book the breakfast service (idempotent: skip when already booked).
      let breakfastBooked = false;
      let breakfastSkipReason: string | null = null;
      if (!breakfastServiceId) {
        breakfastSkipReason = "no_breakfast_service_id";
        log("warn", `Breakfast service not booked for ${res.id}: no breakfastServiceId configured`);
      } else {
        let existingServices: ServiceOfferModel[] | null = null;
        try {
          existingServices = await this.fetchReservationBookedServices(res.id || "");
        } catch (e) {
          // Fail-closed: if we cannot verify, do not risk double-booking.
          breakfastSkipReason = "existing_services_unverifiable";
          log("warn", `Cannot verify existing services for ${res.id}: ${errorMessage(e) || e}`);
        }
        if (existingServices === null) {
          // verification failed — leave the reservation untouched service-wise
        } else {
          const alreadyBooked = (existingServices || []).some((s) => {
            const sid = s?.service?.id ?? s?.serviceId ?? s?.id;
            return typeof sid === "string" && sid === breakfastServiceId;
          });
          if (alreadyBooked) {
            breakfastSkipReason = "breakfast_service_already_booked";
            log("log", `Breakfast service already on ${res.id} — not booking again`);
          } else {
            await this.bookReservationService(res.id || "", {
              serviceId: breakfastServiceId,
              count: guests,
              amount: {
                amount: breakfastAmount,
                currency,
              },
            });
            breakfastBooked = true;
            log(
              "log",
              `Booked breakfast service ${breakfastServiceId} on ${res.id}: ${guests} × ${breakfastAmount.toFixed(2)} ${currency} per night`,
            );
          }
        }
      }
      // Leave a trace in the reservation comment so front desk can see the
      // automation ran. Wording depends on whether the breakfast service
      // actually landed: "getrennt" describes the folio state (true even
      // when the service was already booked before), the NoBreakfast
      // variant fires only when the service is genuinely missing.
      // Read-modify-write; never overwrites, never throws — the money
      // amend already succeeded.
      // Same marker discipline as recalculateCityTaxes: German date +
      // operator-initials suffix (see _automationMarkerLine).
      const splitCommentLine = breakfastBooked || breakfastSkipReason === "breakfast_service_already_booked"
        ? this._automationMarkerLine("recalcCityTax.automationCommentSplit", user)
        : this._automationMarkerLine("recalcCityTax.automationCommentSplitNoBreakfast", user, {
            reason: breakfastSkipReason || "unknown",
          });
      await this._appendReservationComment(res.id || "", splitCommentLine, log);

      result.processed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "processed",
        originalTotal,
        newTotal: projectedNewTotal,
        nights,
        guests,
        breakfastPerNight,
        totalBreakfast,
        breakfastBooked,
        breakfastSkipReason,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: totalNewAccommodation,
        totalCityTax: totalProjectedCityTax,
        currency,
      });
    } catch (err) {
      result.failed++;
      result.results.push({
        reservationId: res.id,
        guestName,
        channel,
        source,
        status: "failed",
        reason: errorMessage(err) || "unknown_error",
        originalTotal,
        newTotal: projectedNewTotal,
        nights,
        guests,
        breakfastPerNight,
        totalBreakfast,
        perNightGross: slicePrices,
        perNightAccommodation: newPerNightPrices,
        perNightCityTax: projectedCityTaxPerNight,
        totalAccommodation: totalNewAccommodation,
        totalCityTax: totalProjectedCityTax,
        currency,
      });
      log("error", `Failed ${res.id} (${guestName}): ${errorMessage(err) || err}`);
    }
  }

  if (typeof onProgress === "function") {
    try { onProgress({ processed: total, total, current: null }); } catch { /* noop */ }
  }

  log(
    "log",
    `Done. processed=${result.processed} skipped=${result.skipped} failed=${result.failed} (dryRun=${dryRun})`,
  );

  return result;
},

};

export default part;
